/* eslint-disable camelcase */
import React, { useState } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material"
import ViewListIcon from "@mui/icons-material/ViewList"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import SearchIcon from "@mui/icons-material/Search"
import Swal from "sweetalert2"
import { showWarning } from "../../../../api"

import ModalLotesIngreso from "./ModalLotesIngreso"
import ModalLotesEgreso from "./ModalLotesEgreso"
import ModalSeriesIngreso from "./ModalSeriesIngreso"
import ModalSeriesEgreso from "./ModalSeriesEgreso"
import ModalStockAgrupado from "./ModalStockAgrupado"

const DetalleArticulosInvGrid = ({
  detalle,
  setFormData,
  tratipo,
  bodcodigo,
  sgastatus,
  tsolInEg,
  listaBodegas = [],
  disableBodegaGrid = false,
  trametodo, // Recibimos el parámetro para evaluar si aplica costo
}) => {
  const [activeRowIndex, setActiveRowIndex] = useState(null)
  const [modalType, setModalType] = useState("")

  const [stockModalOpen, setStockModalOpen] = useState(false)
  const [activeStockArticulo, setActiveStockArticulo] = useState(null)
  const [hasPromptedBodega, setHasPromptedBodega] = useState(false)

  // 1. Ampliamos las transferencias asíncronas para incluir los Préstamos
  const t = String(tsolInEg || "")
    .toUpperCase()
    .trim()
  const isTransferenciaAsincrona = ["SOLEGRLOC", "SOLEGRPRES"].includes(t)

  // 2. Evaluamos si la transacción exige ingreso manual de costos
  const aplicaCosto = trametodo === true || String(trametodo) === "1" || String(trametodo).toLowerCase() === "true"

  const handleBodegaChange = async (index, value) => {
    const val = String(value || "")
      .toUpperCase()
      .trim()

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index].bodcodigo = val
      return { ...prev, detalle: nuevoDetalle }
    })

    if (!hasPromptedBodega && val !== "") {
      const hayOtrasPendientes = detalle.some((row, i) => i !== index && row.cant_pendiente > 0)

      if (hayOtrasPendientes) {
        setHasPromptedBodega(true)

        const confirm = await Swal.fire({
          title: "¿Replicar Bodega?",
          html: `¿Desea asignar la bodega <b>${val}</b> a todos los demás artículos pendientes de la solicitud?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Sí, aplicar a todos",
          cancelButtonText: "No, solo a esta línea",
          confirmButtonColor: "#196C87",
          cancelButtonColor: "#757575",
          reverseButtons: true,
        })

        if (confirm.isConfirmed) {
          setFormData((prev) => {
            const nuevoDetalle = prev.detalle.map((row) => {
              if (row.cant_pendiente > 0) {
                return { ...row, bodcodigo: val }
              }
              return row
            })
            return { ...prev, detalle: nuevoDetalle }
          })
        }
      }
    }
  }

  const handleCantidadChange = (index, value) => {
    const row = detalle[index]
    const permiteDecimales =
      row.artdecimal === true || String(row.artdecimal) === "1" || String(row.artdecimal).toLowerCase() === "true"

    let valStr = String(value)
    let val = 0

    // 3. Validación estricta de decimales según artdecimal
    if (!permiteDecimales) {
      if (valStr.includes(".") || valStr.includes(",")) {
        showWarning(`El artículo ${row.artcodigo} no permite cantidades con decimales.`)
        valStr = valStr.split(".")[0].split(",")[0]
      }
      val = parseInt(valStr, 10) || 0
    } else {
      val = parseFloat(valStr) || 0
    }

    if (val < 0) val = 0

    if (val > row.cant_pendiente) {
      showWarning(`La cantidad a afectar no puede ser mayor a la cantidad pendiente (${row.cant_pendiente}).`)
      val = row.cant_pendiente
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      if (nuevoDetalle[index].cant_afectar !== val) {
        nuevoDetalle[index].lotes = []
        nuevoDetalle[index].series = []
      }
      nuevoDetalle[index].cant_afectar = val
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // Nuevo handler para guardar el costo si aplica
  const handleCostoChange = (index, value) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index].costouni = value
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  const handleOpenModal = (index, type) => {
    const row = detalle[index]

    if (!row.bodcodigo || String(row.bodcodigo).trim() === "") {
      return showWarning(`Primero asigne una Bodega al artículo ${row.artcodigo}.`)
    }

    if (row.cant_afectar <= 0) {
      return showWarning(
        `Primero digite la 'Cantidad a Afectar' en el artículo ${row.artcodigo} antes de asignar ${type}.`,
      )
    }

    setActiveRowIndex(index)
    setModalType(type)
  }

  const handleCloseModal = () => {
    setActiveRowIndex(null)
    setModalType("")
  }

  const handleOpenStock = (row) => {
    setActiveStockArticulo(row)
    setStockModalOpen(true)
  }

  const handleSaveLotes = (lotes) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].lotes = lotes
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  const handleSaveSeries = (series) => {
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[activeRowIndex].series = series
      return { ...prev, detalle: nuevoDetalle }
    })
    handleCloseModal()
  }

  const currentRow = activeRowIndex !== null ? detalle[activeRowIndex] : null
  const baseColSpan = isTransferenciaAsincrona ? 10 : 8
  const totalColSpan = aplicaCosto ? baseColSpan + 1 : baseColSpan

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
            <TableRow>
              <TableCell width="3%">#</TableCell>
              <TableCell width={isTransferenciaAsincrona ? "15%" : "25%"}>Artículo (Código y Descripción)</TableCell>
              <TableCell width="12%">Bodega</TableCell>
              <TableCell width="8%" align="center">
                Cant. Sol.
              </TableCell>

              {/* RENDERIZADO CONDICIONAL DE CABECERAS PARA TRANSFERENCIAS */}
              {isTransferenciaAsincrona ? (
                <>
                  <TableCell width="8%" align="center">
                    Proc. (Egr)
                  </TableCell>
                  <TableCell width="8%" align="center">
                    Pend. (Egr)
                  </TableCell>
                  <TableCell width="8%" align="center">
                    Proc. (Ing)
                  </TableCell>
                  <TableCell width="8%" align="center">
                    Pend. (Ing)
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell width="10%" align="center">
                    Cant. Procesada
                  </TableCell>
                  <TableCell width="10%" align="center">
                    Cant. Pendiente
                  </TableCell>
                </>
              )}

              <TableCell width="10%" align="center">
                Cant. a Afectar
              </TableCell>

              {/* COLUMNA CONDICIONAL DE COSTO UNITARIO */}
              {aplicaCosto && (
                <TableCell width="10%" align="center">
                  Costo Unit.
                </TableCell>
              )}

              <TableCell width="10%" align="center">
                Opciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColSpan} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No hay artículos en el detalle de la solicitud.
                </TableCell>
              </TableRow>
            ) : (
              detalle.map((row, index) => {
                const lotesCompletos =
                  row.lotes &&
                  row.lotes.reduce((acc, curr) => acc + parseFloat(curr.cantidad || 0), 0) ===
                    parseFloat(row.cant_afectar)

                const seriesCompletas = row.series && row.series.length === parseFloat(row.cant_afectar)

                const saldoPendienteLinea = parseFloat(row.cant_pendiente || 0)
                const estaBloqueado = saldoPendienteLinea <= 0
                const permiteDecimales =
                  row.artdecimal === true ||
                  String(row.artdecimal) === "1" ||
                  String(row.artdecimal).toLowerCase() === "true"

                return (
                  <TableRow key={index} sx={{ backgroundColor: estaBloqueado ? "#f0f0f0" : "inherit" }}>
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {row.artcodigo}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ display: "block", lineHeight: 1.1 }}
                          >
                            {row.artdescri}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Autocomplete
                        options={listaBodegas}
                        getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                        value={
                          listaBodegas.find((b) => String(b.value || b.id) === String(row.bodcodigo)) ||
                          (row.bodcodigo ? { id: row.bodcodigo, label: row.bodcodigo } : null)
                        }
                        isOptionEqualToValue={(option, value) =>
                          String(option.value || option.id) === String(value?.id || value?.value)
                        }
                        onChange={(e, newVal) =>
                          handleBodegaChange(index, newVal ? String(newVal.value || newVal.id) : "")
                        }
                        // Bloqueo Combinado: Se bloquea si la línea no tiene saldo o si la regla general dice que hereda de la cabecera
                        disabled={estaBloqueado || disableBodegaGrid}
                        disableClearable
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" size="small" placeholder="Bodega" />
                        )}
                        fullWidth
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {row.sgacansol}
                      </Typography>
                    </TableCell>

                    {/* RENDERIZADO DE LOS CÁLCULOS EXACTOS DE TRANSFERENCIA */}
                    {isTransferenciaAsincrona ? (
                      <>
                        <TableCell align="center">
                          <Typography variant="body2" color="secondary" fontWeight="bold">
                            {row.sgacanegr || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="error" fontWeight="bold">
                            {row.pend_egreso || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="primary" fontWeight="bold">
                            {row.sgacanrec || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="error" fontWeight="bold">
                            {row.pend_ingreso || 0}
                          </Typography>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center">
                          <Typography variant="body2" color="secondary" fontWeight="bold">
                            {tratipo === "INGRESO" ? row.sgacanrec || 0 : row.sgacanegr || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="primary" fontWeight="bold">
                            {row.cant_pendiente}
                          </Typography>
                        </TableCell>
                      </>
                    )}

                    <TableCell align="center">
                      <TextField
                        type="number"
                        variant="outlined"
                        size="small"
                        inputProps={{
                          min: 0,
                          max: saldoPendienteLinea,
                          step: permiteDecimales ? "0.01" : "1",
                        }}
                        value={row.cant_afectar === 0 ? "" : row.cant_afectar}
                        onChange={(e) => handleCantidadChange(index, e.target.value)}
                        disabled={estaBloqueado}
                        fullWidth
                      />
                    </TableCell>

                    {/* RENDERIZADO CONDICIONAL DE COSTO UNITARIO */}
                    {aplicaCosto && (
                      <TableCell align="center">
                        <TextField
                          type="number"
                          variant="outlined"
                          size="small"
                          inputProps={{ min: 0, step: "0.0001" }}
                          value={row.costouni !== undefined && row.costouni !== null ? row.costouni : ""}
                          onChange={(e) => handleCostoChange(index, e.target.value)}
                          disabled={estaBloqueado}
                          fullWidth
                        />
                      </TableCell>
                    )}

                    <TableCell align="center">
                      <Tooltip title="Consultar Stock de este Artículo">
                        <span>
                          <IconButton size="small" onClick={() => handleOpenStock(row)} sx={{ color: "#196C87" }}>
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      {row.artlote !== 0 && (
                        <Tooltip title="Asignar Lotes">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "LOTES")}
                              disabled={estaBloqueado || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !lotesCompletos ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <ViewListIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {row.artserie !== 0 && (
                        <Tooltip title="Asignar Series">
                          <span>
                            <IconButton
                              onClick={() => handleOpenModal(index, "SERIES")}
                              disabled={estaBloqueado || parseFloat(row.cant_afectar) <= 0}
                              sx={{
                                color: parseFloat(row.cant_afectar) > 0 && !seriesCompletas ? "#d32f2f" : "#2E7D32",
                              }}
                            >
                              <FormatListNumberedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODALES */}
      {modalType === "LOTES" && tratipo === "INGRESO" && (
        <ModalLotesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {modalType === "LOTES" && tratipo === "EGRESO" && (
        <ModalLotesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveLotes}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "INGRESO" && (
        <ModalSeriesIngreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {modalType === "SERIES" && tratipo === "EGRESO" && (
        <ModalSeriesEgreso
          open={true}
          onClose={handleCloseModal}
          onSave={handleSaveSeries}
          articulo={currentRow}
          bodcodigo={currentRow?.bodcodigo}
        />
      )}

      {stockModalOpen && activeStockArticulo && (
        <ModalStockAgrupado
          open={stockModalOpen}
          onClose={() => {
            setStockModalOpen(false)
            setActiveStockArticulo(null)
          }}
          articulo={activeStockArticulo}
        />
      )}
    </Box>
  )
}

export default DetalleArticulosInvGrid
